import { useState, type ReactNode } from "react";
import { Boxes, Globe, Loader2, MessagesSquare, Mic } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { ModelPurposeField, StatusIcon } from "@/shared/components/ai/ModelPurposeField";
import { type FieldCheck, type PurposeChoice } from "@/shared/types/aiModels";
import { type ToolOption } from "@/shared/components/ui/tool-select";
import { isModelless, type ModelPurpose } from "@/shared/utils/modelCatalog";

/**
 * Um bloco de escolha de modelo por finalidade: acordeon + botão de salvar próprio.
 *
 * Extraído das duas telas que faziam a mesma coisa com aparências diferentes (AB#85617): a da
 * organização era `Card` com campos soltos, a de superadmin já era acordeon. Com a
 * contingência, cada tela passa a ter DOIS blocos — quatro marcações quase idênticas em dois
 * arquivos, que divergiriam no primeiro ajuste de estilo. Aqui o estilo é o mesmo por
 * construção, não por cópia.
 *
 * O componente não sabe se atende organização ou plataforma, nem se é principal ou
 * contingência: recebe as finalidades, os valores, o veredito da sonda e o que fazer ao
 * salvar. É isso que permite os quatro usos.
 *
 * O texto vem de fora porque as duas telas têm namespaces de i18n diferentes, e um mapa de
 * chaves aqui dentro amarraria o componente a elas.
 */

/** Os rótulos de uma finalidade. Quem chama resolve o i18n. */
export type PurposeLabels = {
  section: string;
  help: string;
  provider: string;
  model: string;
};

const ICONES: Record<ModelPurpose, ReactNode> = {
  conversational: <MessagesSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />,
  embedding: <Boxes className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />,
  transcription: <Mic className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />,
  web_search: <Globe className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />,
};

/**
 * Logo do provedor ao lado do modelo.
 *
 * O nome do modelo sozinho não diz de qual integração ele vem, e a mesma organização pode ter
 * mais de um provedor cadastrado. Logo que não carrega some em vez de deixar quadro quebrado.
 */
function LogoDoProvedor({ provider }: { provider?: ToolOption }) {
  const [falhou, setFalhou] = useState(false);
  if (!provider?.logo_url || falhou) return null;
  return (
    <img
      src={provider.logo_url}
      alt=""
      aria-hidden
      onError={() => setFalhou(true)}
      className="h-4 w-4 shrink-0 rounded-sm object-contain"
    />
  );
}

export type ModelPurposeAccordionProps<P extends ModelPurpose> = {
  /** As finalidades a exibir, na ordem. */
  purposes: readonly P[];
  labels: Record<P, PurposeLabels>;
  values: Record<P, PurposeChoice>;
  checks: Record<P, FieldCheck>;
  /** Modelos que cada finalidade pode escolher, vindos do que o provedor declara. */
  catalogs: Record<P, string[]>;
  providers: ToolOption[];
  /**
   * Provedores próprios de uma finalidade, no lugar de `providers` (AB#86885).
   *
   * A busca web não escolhe entre os provedores de LLM: escolhe entre as integrações que
   * declaram a capacidade, e a Tavily nem é LLM.
   */
  providersByPurpose?: Partial<Record<P, ToolOption[]>>;
  loadingProviders?: boolean;
  /** Quais seções estão abertas. Controlado por quem chama, que decide o estado inicial. */
  open: string[];
  onOpenChange: (open: string[]) => void;
  onChange: (purpose: P, patch: Partial<PurposeChoice>) => void;
  /** Selo de finalidade sem modelo escolhido. */
  notConfiguredLabel: string;
  selectModelPlaceholder: string;
  /**
   * Conteúdo extra dentro de uma finalidade — hoje o `prompt_context` da transcrição.
   *
   * Fica como render prop, e não como campo do contrato: é de UMA finalidade de UMA tela, e
   * um campo assim no contrato obrigaria os outros três usos a ignorá-lo.
   */
  extra?: (purpose: P) => ReactNode;
  /**
   * Omitir os quatro campos abaixo renderiza o acordeon SEM botão de salvar.
   *
   * Serve a quem compõe vários acordeões numa decisão só — a cadeia de contingência, onde
   * cada elo é um acordeão e o salvar pertence ao conjunto, não a cada elo.
   */
  onSubmit?: () => void;
  saving?: boolean;
  /** `false` bloqueia o salvar — nada mudou, ou há finalidade configurada e não aprovada. */
  canSave?: boolean;
  saveLabel?: string;
  savingLabel?: string;
};

export function ModelPurposeAccordion<P extends ModelPurpose>({
  purposes,
  labels,
  values,
  checks,
  catalogs,
  providers,
  providersByPurpose,
  loadingProviders,
  open,
  onOpenChange,
  onChange,
  notConfiguredLabel,
  selectModelPlaceholder,
  extra,
  onSubmit,
  saving,
  canSave,
  saveLabel,
  savingLabel,
}: ModelPurposeAccordionProps<P>) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
      className="space-y-4"
    >
      {/* Uma seção por finalidade. Em lista corrida o Save ficava abaixo da dobra, e a
          finalidade que mais se mexe era a mais distante. Fechada, cada seção continua
          dizendo o que importa — o modelo escolhido e o veredito do teste. */}
      <Accordion
        type="multiple"
        value={open}
        onValueChange={onOpenChange}
        className="space-y-2"
      >
        {purposes.map((purpose) => {
          const opcoes = providersByPurpose?.[purpose] ?? providers;
          const escolhido = opcoes.find((p) => p.id === values[purpose].integrationId);
          // Sem modelo a escolher, o cabeçalho nomeia o provedor — "search" não diz nada.
          const resumo = isModelless(catalogs[purpose])
            ? (escolhido?.name ?? values[purpose].model)
            : values[purpose].model;
          return (
            <AccordionItem
              key={purpose}
              value={purpose}
              className="rounded-lg border bg-card px-4 transition-colors last:border-b data-[state=open]:border-primary/40"
            >
              <AccordionTrigger className="py-3 hover:no-underline">
                {/* Fechado, o cabeçalho precisa bastar: para que serve a finalidade, qual
                    modelo atende hoje e se ele respondeu ao teste. */}
                <div className="flex min-w-0 flex-1 items-start gap-3 pr-3 text-left">
                  {ICONES[purpose]}

                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{labels[purpose].section}</span>
                    <span className="mt-0.5 block text-xs font-normal leading-snug text-muted-foreground">
                      {labels[purpose].help}
                    </span>
                  </div>

                  <span className="flex shrink-0 items-center gap-2 pt-0.5">
                    {values[purpose].model ? (
                      <>
                        <LogoDoProvedor
                          provider={escolhido}
                        />
                        <code className="max-w-[14rem] truncate rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                          {resumo}
                        </code>
                      </>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-amber-500/50 text-[10px] font-normal text-amber-600 dark:text-amber-500"
                      >
                        {notConfiguredLabel}
                      </Badge>
                    )}
                    <StatusIcon check={checks[purpose]} />
                  </span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="space-y-3 border-t pt-4">
                <ModelPurposeField
                  providerLabel={labels[purpose].provider}
                  modelLabel={labels[purpose].model}
                  // A descrição da finalidade vive no cabeçalho da seção, onde é lida mesmo
                  // com ela fechada — repeti-la aqui seria dizer duas vezes.
                  modelPlaceholder={selectModelPlaceholder}
                  providers={opcoes}
                  providerId={values[purpose].integrationId}
                  onProviderChange={(integrationId) => onChange(purpose, { integrationId })}
                  model={values[purpose].model}
                  onModelChange={(model) => onChange(purpose, { model })}
                  models={catalogs[purpose]}
                  check={checks[purpose]}
                  loadingProviders={loadingProviders}
                />

                {extra?.(purpose)}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {onSubmit && (
        <Button type="submit" disabled={saving || !canSave} className="w-full sm:w-auto">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {savingLabel}
            </>
          ) : (
            saveLabel
          )}
        </Button>
      )}
    </form>
  );
}
